import React, {useState, useRef, useEffect, useMemo, useCallback} from 'react';
import {useTransition, config} from '@react-spring/web';
import {LuMenu, LuX} from 'react-icons/lu';

import useStore from './useStore';
import Button from './Button';

//

const modes = ['focus', 'current', 'all'];
export const icons = [LuMenu, LuMenu, LuX];

const Header = props => {
  const elements = useRef({});
  const sizes = useRef(null);
  const [ready, setReady] = useState(false);
  const [height, setHeight] = useState(0);
  const ref = useRef();
  const title = 'hamburger';
  const codex = useStore(s => s.codex);
  const set = useStore(s => s.set);
  const width = props.width;
  const tags = props.tags;
  const categories = props.categories;
  const current = useStore(s => s.tags);
  const masterTagBreadcrumb = useStore(s => s.masterTagBreadcrumb);
  const mastertags = useStore(s => s.mastertags);

  // Navigation functions
  const navigateToMasterTag = slug => {
    const newBreadcrumb = [...masterTagBreadcrumb, slug];
    set({masterTagBreadcrumb: newBreadcrumb});
  };

  const navigateBack = useCallback(() => {
    if (masterTagBreadcrumb.length > 1) {
      const newBreadcrumb = masterTagBreadcrumb.slice(0, -1);
      set({masterTagBreadcrumb: newBreadcrumb});
    }
  }, [masterTagBreadcrumb, set]);

  // Get current master tag
  const currentMasterTag = masterTagBreadcrumb[masterTagBreadcrumb.length - 1];

  // Helper function to get display name from slug
  const getDisplayName = slug => {
    if (slug === 'home') return 'Home';
    return slug.charAt(0).toUpperCase() + slug.slice(1);
  };

  // Build all possible menu items with visibility flags
  const allMenuItems = useMemo(() => {
    const items = [];
    const hasMasterTags = mastertags && Object.keys(mastertags).length > 0;

    // If we don't have tags data yet, return empty (loading state)
    if (!Array.isArray(tags) || tags.length === 0) {
      return items;
    }

    // Create navigation items for each master tag level
    if (hasMasterTags) {
      Object.keys(mastertags).forEach(slug => {
        // Add disabled tag showing current level
        items.push({
          type: 'disabled',
          name: getDisplayName(slug),
          slug: slug,
          uniqueKey: `disabled-${slug}`, // Unique key for React
          masterTagLevel: slug,
          visible: masterTagBreadcrumb.includes(slug) && currentMasterTag === slug && masterTagBreadcrumb.length > 1,
        });
      });

      // Add back button (shows for all non-root levels)
      items.push({
        type: 'back',
        name: 'back',
        uniqueKey: 'back-button', // Unique key for React
        callback: navigateBack,
        masterTagLevel: 'navigation',
        visible: masterTagBreadcrumb.length > 1,
      });
    }

    // Add all tags with their visibility logic
    tags.forEach(tag => {
      if (tag.prefix === '—') {
        // Category tag
        items.push({
          type: 'category',
          name: tag.name,
          slug: tag.slug,
          uniqueKey: `category-${tag.slug}`, // Unique key for React
          masterTagLevel: 'content',
          visible: true, // Will be filtered in animated items
          ...(tag.color && {color: tag.color}),
          ...(tag.color_text && {color_text: tag.color_text}),
        });
      } else {
        // Regular tag
        items.push({
          type: 'link',
          name: tag.name,
          slug: tag.slug,
          uniqueKey: `link-${tag.slug}`, // Unique key for React
          link: `/tag/${tag.slug}/`,
          masterTagLevel: 'content',
          visible: true, // Will be filtered in animated items
          ...(tag.color && {color: tag.color}),
          ...(tag.color_text && {color_text: tag.color_text}),
        });
      }
    });

    // Add all master tags with their visibility logic
    if (hasMasterTags) {
      Object.keys(mastertags).forEach(slug => {
        items.push({
          type: 'mastertag',
          name: getDisplayName(slug),
          slug: slug,
          uniqueKey: `mastertag-${slug}`, // Unique key for React
          callback: () => navigateToMasterTag(slug),
          masterTagLevel: mastertags[slug].parent || 'home',
          visible: true, // Will be filtered in animated items
        });
      });
    }

    // Add the menu button to all items for sizing
    items.unshift({type: 'menu', name: 'main', uniqueKey: 'menu-main'});

    return items;
  }, [mastertags, tags, categories, masterTagBreadcrumb, navigateBack]);

  // Filter items based on current master tag context
  const visibleMenuItems = useMemo(() => {
    const hasMasterTags = mastertags && Object.keys(mastertags).length > 0;
    const currentMasterTagData = hasMasterTags ? mastertags[currentMasterTag] : null;
    const currentTagSlugs = currentMasterTagData?.tags ? (Array.isArray(currentMasterTagData.tags) ? currentMasterTagData.tags : Object.values(currentMasterTagData.tags)) : [];

    return allMenuItems.filter(item => {
      // Skip menu button - it's handled separately
      if (item.type === 'menu') {
        return false;
      }

      // Back button - show when not at root level
      if (item.type === 'back') {
        return masterTagBreadcrumb.length > 1;
      }

      // Disabled tags (current level indicators) - show for current master tag when not at root
      if (item.type === 'disabled') {
        return masterTagBreadcrumb.includes(item.slug) && currentMasterTag === item.slug && masterTagBreadcrumb.length > 1;
      }

      // Content items (tags and categories)
      if (item.masterTagLevel === 'content') {
        if (item.type === 'category') {
          // Category visible if it has relevant subtags
          if (!categories || !categories[item.slug]) return false;
          if (!hasMasterTags) return true;
          return categories[item.slug].some(subtagSlug => currentTagSlugs.includes(subtagSlug));
        } else {
          // Regular tag visible if in current master tag or no master tags exist
          return !hasMasterTags || currentTagSlugs.includes(item.slug);
        }
      }

      // Master tags visible if they are children of current master tag
      if (item.type === 'mastertag') {
        return item.masterTagLevel === currentMasterTag && item.slug !== currentMasterTag;
      }

      return false;
    });
  }, [allMenuItems, mastertags, masterTagBreadcrumb, currentMasterTag, categories]);

  // all tags
  const all = useMemo(() => {
    // Find the menu button from allMenuItems and combine with visible items
    const menuButton = allMenuItems.find(item => item.type === 'menu');
    return [menuButton, ...visibleMenuItems].filter(Boolean);
  }, [allMenuItems, visibleMenuItems]);

  // tags to show (according to current mode)
  const items = useMemo(() => {
    let items = [];

    if (modes[codex] === 'focus') {
      const menuButton = allMenuItems.find(item => item.type === 'menu');
      items = [menuButton].filter(Boolean);
    }
    if (modes[codex] === 'current') items = current ?? [];
    if (modes[codex] === 'all') items = all ?? [];

    return items;
  }, [codex, title, all, current, allMenuItems]);

  // compute tags positions
  const animated = useMemo(() => {
    if (!sizes.current || !ready) return [];

    return items.reduce((acc, val, idx) => {
      const s = sizes.current.find(o => o.name === val.name);
      const w = s?.w || 0;
      const h = s?.h || 0;
      const prev = idx > 0 && acc[idx - 1];
      const xx = prev?.xx || 0;
      const yy = prev?.yy || 0;
      const nl = xx + w >= width - 16;
      const x = nl ? 0 : xx;
      const y = nl ? yy + h : yy;

      return [...acc, {...val, x, y, w, h, xx: x + w, yy: y}];
    }, []);
  }, [ready, items, width]);

  // set container height
  useEffect(() => {
    if (!animated.length) return;

    const last = animated[animated.length - 1];
    const next = last.yy + last.h;

    const timer = setTimeout(() => setHeight(next), next < height ? 2000 : 0);

    return () => clearTimeout(timer);
  }, [animated, height]);

  // reset scroll
  useEffect(() => {
    const timer = setTimeout(() => {
      ref.current?.scrollTo({top: 0, behavior: 'smooth'});
    }, 500);

    return () => clearTimeout(timer);
  }, [items]);

  // reset tags sizes if window width has changed or new items added
  useEffect(() => {
    setReady(false);
  }, [props.width]);

  // get all tags sizes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!Array.isArray(tags) || !tags.length || ready) return;

      sizes.current = allMenuItems.map(o => {
        const b = elements.current[o.name]?.getBoundingClientRect();
        return {name: o.name, w: b?.width, h: b?.height};
      }, []);

      setReady(true);
    }, 250);

    return () => clearTimeout(timer);
  }, [tags, ready, allMenuItems]);

  const transitions = useTransition(animated, {
    from: item => ({transform: `translate3d(${item.x}px,${item.y - item.h}px,0)`, opacity: 0}),
    enter: item => ({transform: `translate3d(${item.x}px,${item.y}px,0)`, opacity: 1}),
    leave: item => ({transform: `translate3d(${item.x}px,${item.y - item.h}px,0)`, opacity: 0}),
    trail: 10,
    config: config.stiff,
  });

  return (
    <div ref={ref} className="relative top left width header-container">
      {!ready ? (
        <div className="p-h flex flex-wrap" style={{opacity: 0}}>
          {allMenuItems.map(tag => (
            <Button key={tag.uniqueKey} ref={el => (elements.current[tag.name] = el)} tag={tag} />
          ))}
        </div>
      ) : (
        <div className="p-h relative tags-wrapper">
          <div style={{height}}>
            {transitions((style, tag) => (
              <Button style={{...style, position: 'absolute', willChange: 'transform, opacity'}} tag={tag} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;
